import { useEffect, useMemo, useState } from 'react';
import { GripVertical, Plus, Trash2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  ColorPreset,
  ColumnDataType,
  ColumnDef,
  ConditionalFormatRule,
  FormatOperator,
} from './types';
import { allowedOperators, COLOR_PRESETS, COLOR_PRESET_ORDER, operatorLabel } from './formats';
import { CONDITIONAL_ICONS, ConditionalIcon } from './ConditionalIcon';
import { RelativeDateInput } from './RelativeDateInput';

interface ConditionalFormatDialogProps {
  open: boolean;
  column: ColumnDef | null;
  uniqueValues: string[];
  existingRules: ConditionalFormatRule[] | undefined;
  onApply: (rules: ConditionalFormatRule[]) => void;
  onRemove: () => void;
  onOpenChange: (open: boolean) => void;
}

function makeId(): string {
  return `r_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function makeDefaultRule(operators: FormatOperator[]): ConditionalFormatRule {
  return {
    id: makeId(),
    operator: operators[0],
    value: '',
    bgColor: 'yellow',
    textColor: undefined,
    iconColor: undefined,
    applyToRow: false,
  };
}

export function ConditionalFormatDialog({
  open,
  column,
  uniqueValues,
  existingRules,
  onApply,
  onRemove,
  onOpenChange,
}: ConditionalFormatDialogProps) {
  const dataType: ColumnDataType = column?.dataType ?? 'string';
  const operators = useMemo(() => allowedOperators(dataType), [dataType]);

  const [rules, setRules] = useState<ConditionalFormatRule[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const seed = (existingRules ?? []).map((r) => ({ ...r, id: r.id ?? makeId() }));
    setRules(seed);
    setActiveId(seed[0]?.id ?? null);
  }, [open, existingRules]);

  if (!column) return null;

  const updateRule = (id: string, patch: Partial<ConditionalFormatRule>) =>
    setRules((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const addRule = () => {
    const fresh = makeDefaultRule(operators);
    setRules((prev) => [fresh, ...prev]);
    setActiveId(fresh.id);
  };

  const deleteRule = (id: string) => {
    setRules((prev) => prev.filter((r) => r.id !== id));
    setActiveId((cur) => (cur === id ? null : cur));
  };

  const moveRule = (draggedId: string, targetId: string | null) => {
    setRules((prev) => {
      const next = prev.slice();
      const fromIdx = next.findIndex((r) => r.id === draggedId);
      if (fromIdx === -1) return prev;
      const [moved] = next.splice(fromIdx, 1);
      if (!targetId) {
        next.push(moved);
      } else {
        const toIdx = next.findIndex((r) => r.id === targetId);
        next.splice(toIdx === -1 ? next.length : toIdx, 0, moved);
      }
      return next;
    });
  };

  const active = rules.find((r) => r.id === activeId) ?? null;

  const canApply = (() => {
    if (rules.length === 0) return true; // allowed: apply empties = remove
    for (const r of rules) {
      if (!r.value.trim()) return false;
      if (r.operator === 'between' && !(r.value2 && r.value2.trim())) return false;
      if (!r.bgColor && !r.textColor && !r.iconColor && !r.icon) return false;
    }
    return true;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl p-0 gap-0 max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-slate-100">
          <DialogTitle className="text-base font-semibold text-slate-900">
            Conditional Format — {column.label}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500 mt-0.5">
            Rules evaluate top-to-bottom. The first match wins.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-1 min-h-0">
          {/* Rule list */}
          <div className="w-[14rem] shrink-0 border-r border-slate-100 flex flex-col">
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
              <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                Rules
              </span>
              <button
                onClick={addRule}
                className="inline-flex items-center gap-1 px-2 h-7 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-md"
              >
                <Plus className="w-3 h-3" />
                Add
              </button>
            </div>

            <div
              className="flex-1 overflow-y-auto p-2 space-y-1"
              onDragOver={(e) => {
                if (draggingId) e.preventDefault();
              }}
              onDrop={(e) => {
                if (!draggingId) return;
                e.preventDefault();
                moveRule(draggingId, dropTargetId);
                setDraggingId(null);
                setDropTargetId(null);
              }}
            >
              {rules.length === 0 && (
                <div className="text-xs text-slate-400 text-center py-8">
                  No rules yet.
                  <br />
                  Click <span className="font-medium">Add</span> to create one.
                </div>
              )}
              {rules.map((rule) => (
                <RuleListItem
                  key={rule.id}
                  rule={rule}
                  dataType={dataType}
                  isActive={activeId === rule.id}
                  isDropTarget={dropTargetId === rule.id && draggingId !== rule.id}
                  onSelect={() => setActiveId(rule.id)}
                  onDelete={() => deleteRule(rule.id)}
                  onDragStart={() => setDraggingId(rule.id)}
                  onDragEnd={() => {
                    setDraggingId(null);
                    setDropTargetId(null);
                  }}
                  onDragOver={() => {
                    if (draggingId && draggingId !== rule.id) setDropTargetId(rule.id);
                  }}
                />
              ))}
            </div>
          </div>

          {/* Rule editor */}
          <div className="flex-1 overflow-y-auto p-5">
            {active ? (
              <RuleEditor
                rule={active}
                dataType={dataType}
                operators={operators}
                uniqueValues={uniqueValues}
                onChange={(patch) => updateRule(active.id, patch)}
              />
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-slate-400">
                Select a rule on the left, or add a new one to get started.
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-slate-100 bg-slate-50/50">
          <Button
            variant="ghost"
            onClick={() => {
              onRemove();
              onOpenChange(false);
            }}
            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 h-8 text-sm"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
            Remove all
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="h-8 text-sm">
              Cancel
            </Button>
            <Button
              disabled={!canApply}
              onClick={() => {
                onApply(rules);
                onOpenChange(false);
              }}
              className="bg-slate-900 hover:bg-slate-800 text-white h-8 text-sm"
            >
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface RuleListItemProps {
  rule: ConditionalFormatRule;
  dataType: ColumnDataType;
  isActive: boolean;
  isDropTarget: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOver: () => void;
}

function RuleListItem({
  rule,
  dataType,
  isActive,
  isDropTarget,
  onSelect,
  onDelete,
  onDragStart,
  onDragEnd,
  onDragOver,
}: RuleListItemProps) {
  const bg = rule.bgColor ?? rule.color;
  const text = rule.textColor ?? rule.color;
  const iconCol = rule.iconColor ?? rule.color;
  const summary = ruleSummary(rule, dataType);
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onDragOver();
      }}
      onClick={onSelect}
      className={cn(
        'flex items-start gap-1.5 px-2 py-1.5 rounded-md cursor-pointer border transition-colors',
        isActive
          ? 'bg-slate-100 border-slate-300'
          : 'bg-white border-transparent hover:bg-slate-50',
        isDropTarget && 'border-blue-300 bg-blue-50/50'
      )}
    >
      <GripVertical className="w-3.5 h-3.5 text-slate-300 mt-0.5 cursor-grab active:cursor-grabbing shrink-0" />
      <div className="flex-1 min-w-0">
        <div
          className={cn(
            'text-[11px] font-medium px-1.5 py-0.5 rounded inline-flex items-center gap-1 mb-0.5',
            bg ? COLOR_PRESETS[bg].bg : 'bg-slate-50',
            text ? COLOR_PRESETS[text].text : 'text-slate-500'
          )}
        >
          {rule.icon && (
            <ConditionalIcon name={rule.icon} color={iconCol} className="w-3 h-3" />
          )}
          <span className="truncate">Sample</span>
        </div>
        <div className="text-[10px] text-slate-500 truncate">{summary}</div>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="text-slate-300 hover:text-rose-600 p-0.5 shrink-0"
        title="Delete rule"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

function ruleSummary(rule: ConditionalFormatRule, dataType: ColumnDataType): string {
  const op = operatorLabel(rule.operator, dataType);
  const val = rule.value || '?';
  if (rule.operator === 'between') return `${op} ${val} & ${rule.value2 ?? '?'}`;
  return `${op} ${val}`;
}

interface RuleEditorProps {
  rule: ConditionalFormatRule;
  dataType: ColumnDataType;
  operators: FormatOperator[];
  uniqueValues: string[];
  onChange: (patch: Partial<ConditionalFormatRule>) => void;
}

function RuleEditor({ rule, dataType, operators, uniqueValues, onChange }: RuleEditorProps) {
  return (
    <div className="space-y-5">
      {/* Condition */}
      <Section title="Condition">
        <div className="grid grid-cols-[8rem_1fr] gap-2 items-center">
          <Select
            value={rule.operator}
            onValueChange={(v) =>
              onChange({ operator: v as FormatOperator, value2: undefined })
            }
          >
            <SelectTrigger className="h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {operators.map((op) => (
                <SelectItem key={op} value={op}>
                  {operatorLabel(op, dataType)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <RuleValueInput
            dataType={dataType}
            operator={rule.operator}
            value={rule.value}
            value2={rule.value2}
            uniqueValues={uniqueValues}
            onChange={(v, v2) => onChange({ value: v, value2: v2 })}
          />
        </div>
      </Section>

      {/* Style */}
      <Section title="Style">
        <div className="space-y-3">
          <ColorRow
            label="Background"
            value={rule.bgColor}
            onChange={(c) => onChange({ bgColor: c })}
          />
          <ColorRow
            label="Text"
            value={rule.textColor}
            onChange={(c) => onChange({ textColor: c })}
          />
          <ColorRow
            label="Icon"
            value={rule.iconColor}
            onChange={(c) => onChange({ iconColor: c })}
          />
        </div>
      </Section>

      {/* Icon */}
      <Section title="Icon">
        <div className="grid grid-cols-9 gap-1.5">
          <button
            type="button"
            onClick={() => onChange({ icon: undefined, hideValue: false })}
            className={cn(
              'h-9 rounded-md border text-[10px] text-slate-500',
              !rule.icon
                ? 'bg-slate-100 border-slate-300'
                : 'bg-white border-slate-200 hover:bg-slate-50'
            )}
            title="No icon"
          >
            none
          </button>
          {CONDITIONAL_ICONS.map((i) => {
            const selected = rule.icon === i.id;
            return (
              <button
                key={i.id}
                type="button"
                onClick={() => onChange({ icon: i.id })}
                className={cn(
                  'h-9 flex items-center justify-center rounded-md border',
                  selected
                    ? 'bg-slate-100 border-slate-300'
                    : 'bg-white border-slate-200 hover:bg-slate-50'
                )}
                title={i.label}
              >
                <ConditionalIcon name={i.id} color={rule.iconColor ?? rule.bgColor ?? 'gray'} />
              </button>
            );
          })}
        </div>
        {rule.icon && (
          <label className="flex items-center gap-2 mt-3 cursor-pointer">
            <Checkbox
              checked={!!rule.hideValue}
              onCheckedChange={(v) => onChange({ hideValue: !!v })}
            />
            <span className="text-sm text-slate-700">Hide cell value (show only icon)</span>
          </label>
        )}
      </Section>

      {/* Preview */}
      <Section title="Preview">
        <PreviewCell rule={rule} />
      </Section>

      <label className="flex items-center gap-2 cursor-pointer">
        <Checkbox
          checked={rule.applyToRow}
          onCheckedChange={(v) => onChange({ applyToRow: !!v })}
        />
        <span className="text-sm text-slate-700">Apply background to entire row</span>
      </label>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2">
        {title}
      </h3>
      {children}
    </section>
  );
}

function ColorRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: ColorPreset | undefined;
  onChange: (next: ColorPreset | undefined) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-slate-500 w-20 shrink-0">{label}</span>
      <button
        type="button"
        onClick={() => onChange(undefined)}
        className={cn(
          'h-7 px-2 rounded-md border text-[10px] font-medium',
          !value
            ? 'bg-slate-100 border-slate-300 text-slate-700'
            : 'bg-white border-slate-200 text-slate-400 hover:bg-slate-50'
        )}
      >
        none
      </button>
      <div className="flex items-center gap-1 flex-wrap">
        {COLOR_PRESET_ORDER.map((preset) => {
          const p = COLOR_PRESETS[preset];
          const selected = value === preset;
          return (
            <button
              key={preset}
              type="button"
              onClick={() => onChange(preset)}
              title={preset}
              className={cn(
                'w-6 h-6 rounded-md border-2 transition-all',
                p.swatch,
                selected
                  ? 'ring-2 ring-offset-1 ring-slate-900 scale-110'
                  : 'opacity-80 hover:opacity-100'
              )}
            />
          );
        })}
      </div>
    </div>
  );
}

function PreviewCell({ rule }: { rule: ConditionalFormatRule }) {
  const bg = rule.bgColor;
  const text = rule.textColor;
  return (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm font-medium',
        bg ? COLOR_PRESETS[bg].bg : 'bg-white',
        text ? COLOR_PRESETS[text].text : 'text-slate-900',
        bg ? COLOR_PRESETS[bg].border : 'border-slate-200'
      )}
    >
      {rule.icon && <ConditionalIcon name={rule.icon} color={rule.iconColor ?? bg ?? 'gray'} />}
      {(!rule.icon || !rule.hideValue) && <span>Sample value</span>}
    </div>
  );
}

interface RuleValueInputProps {
  dataType: ColumnDataType;
  operator: FormatOperator;
  value: string;
  value2: string | undefined;
  uniqueValues: string[];
  onChange: (v: string, v2?: string) => void;
}

function RuleValueInput({
  dataType,
  operator,
  value,
  value2,
  uniqueValues,
  onChange,
}: RuleValueInputProps) {
  const between = operator === 'between';

  if (dataType === 'date') {
    if (between) {
      return (
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <RelativeDateInput value={value} onChange={(v) => onChange(v, value2)} />
          </div>
          <span className="text-sm text-slate-500">and</span>
          <div className="flex-1 min-w-0">
            <RelativeDateInput value={value2 ?? ''} onChange={(v) => onChange(value, v)} />
          </div>
        </div>
      );
    }
    return (
      <RelativeDateInput value={value} onChange={(v) => onChange(v, undefined)} />
    );
  }

  if (between) {
    return (
      <div className="flex items-center gap-2">
        <Input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value, value2)}
          className="flex-1 min-w-0 h-9"
        />
        <span className="text-sm text-slate-500">and</span>
        <Input
          type="number"
          value={value2 ?? ''}
          onChange={(e) => onChange(value, e.target.value)}
          className="flex-1 min-w-0 h-9"
        />
      </div>
    );
  }

  if (dataType === 'enum') {
    return (
      <Select value={value} onValueChange={(v) => onChange(v, undefined)}>
        <SelectTrigger className="h-9 text-sm">
          <SelectValue placeholder="Pick a value" />
        </SelectTrigger>
        <SelectContent>
          {uniqueValues.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-400">No values yet</div>
          ) : (
            uniqueValues.map((v) => (
              <SelectItem key={v} value={v}>
                {v || '(empty)'}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
    );
  }

  if (dataType === 'number') {
    return (
      <Input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value, undefined)}
        placeholder="Value"
        className="h-9"
      />
    );
  }

  return (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value, undefined)}
      placeholder="Value"
      className="h-9"
    />
  );
}
